import React, { useState, useEffect, useRef } from 'react';
import { usePresence } from './presence-context';
import { isChatActive, getIsEnterTimeoutActive } from './Chat'; // Import the chat active state

const MASTER_SPRITE_URL = "https://cursor-party.h80h.xyz/cursor/sheet.png";

// Import cursor configs exactly matching cursor.tsx
const CURSOR_CONFIGS = [
  { spriteY: 40, chatColor: "#ddd53c" },  // 951
  { spriteY: 160, chatColor: "#8abade" }, // 8292
  { spriteY: 200, chatColor: "#88bbaa" }, // 9589
  { spriteY: 360, chatColor: "#9badb7" }, // 12162
  { spriteY: 0, chatColor: "#df7126" },   // 647
  { spriteY: 80, chatColor: "#bf9ff2" },  // 1669
  { spriteY: 120, chatColor: "#4ba1e4" }, // 3925
  { spriteY: 240, chatColor: "#ffb100" }, // 10334
  { spriteY: 280, chatColor: "#d6869e" }, // 11758
  { spriteY: 320, chatColor: "#5b6ee1" }, // 12011
];

// Cursors 647 (spriteY 0) and 10334 (spriteY 240) animate at 500ms/frame
// instead of the 80ms/frame used by every other cursor.
const SLOW_FRAME_SPRITE_Y = new Set([0, 240]);

interface CursorPreviewProps {
  onSwitchCursor: () => void;
}

export default function CursorPreview({ onSwitchCursor }: CursorPreviewProps) {
  const [cursorIndex, setCursorIndex] = useState(0);
  const [previousX, setPreviousX] = useState<number | null>(null);
  const [previousY, setPreviousY] = useState<number | null>(null);
  const [isFlipped, setIsFlipped] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const [isReady, setIsReady] = useState(false); // Track if everything is ready to display
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const movementTimeoutRef = useRef<number | null>(null);

  const { myself } = usePresence((state) => ({
    myself: state.myself,
  }));

  // Get current cursor position
  const cursor = myself?.presence?.cursor;

  // Initialize cursor index and mark data as loaded
  useEffect(() => {
    const savedIndex = localStorage.getItem("cursorIndex");
    if (savedIndex) {
      setCursorIndex(parseInt(savedIndex));
    } else if (myself?.presence?.cursorIndex !== undefined) {
      setCursorIndex(myself.presence.cursorIndex);
    }
    
    // Mark data as loaded after initial setup
    setDataLoaded(true);
  }, [myself?.presence?.cursorIndex]);

  // Update cursor index when it changes in presence
  useEffect(() => {
    if (myself?.presence?.cursorIndex !== undefined) {
      setCursorIndex(myself.presence.cursorIndex);
    }
  }, [myself?.presence?.cursorIndex]);

  // Get current cursor configuration
  const currentCursor = CURSOR_CONFIGS[cursorIndex % CURSOR_CONFIGS.length];
  const isSlowFrame = SLOW_FRAME_SPRITE_Y.has(currentCursor.spriteY);

  // Preload master sprite and wait for it to load
  useEffect(() => {
    const spriteImg = new Image();
    spriteImg.onload = () => setImagesLoaded(true);
    spriteImg.onerror = () => setImagesLoaded(true); // Prevent hanging
    spriteImg.src = MASTER_SPRITE_URL;
  }, []);

  // Set ready state when both images and data are loaded
  useEffect(() => {
    if (imagesLoaded && dataLoaded) {
      // Add a small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        setIsReady(true);
      }, 16); // One frame delay
      
      return () => clearTimeout(timer);
    }
  }, [imagesLoaded, dataLoaded]);

  // Track movement direction and detect if cursor is moving
  useEffect(() => {
    if (!cursor) return;
    
    if (previousX !== null && previousY !== null) {
      const deltaX = cursor.x - previousX;
      const deltaY = cursor.y - previousY;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      
      // More sensitive flipping - flip on any horizontal movement
      if (Math.abs(deltaX) > 0.001) {
        setIsFlipped(deltaX < 0); // Flip when moving left (deltaX negative)
      }
      
      // Detect ANY movement - even smallest changes trigger walking
      if (distance > 0) {
        setIsMoving(true);
        
        // Clear existing timeout
        if (movementTimeoutRef.current) {
          clearTimeout(movementTimeoutRef.current);
        }
        
        // Set timeout to mark cursor as stationary after no movement
        movementTimeoutRef.current = setTimeout(() => {
          setIsMoving(false);
        }, 100); // 100ms delay before considering cursor stationary
      }
    }
    
    // Always update previous positions when cursor exists
    setPreviousX(cursor.x);
    setPreviousY(cursor.y);
  }, [cursor?.x, cursor?.y]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (movementTimeoutRef.current) {
        clearTimeout(movementTimeoutRef.current);
      }
    };
  }, []);

  // Main glass container style
  const glassStyle = {
    background: 'rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(2px) saturate(180%)',
    WebkitBackdropFilter: 'blur(2px) saturate(180%)',
    border: '1px solid rgba(255, 255, 255, 0.8)',
    padding: '2px',
    // Kept your existing shadow, but appended the 0.5px matching rgba sealant to plug the border gap
    boxShadow: '0 8px 32px rgba(31, 38, 135, 0.1), inset 0 4px 20px rgba(255, 255, 255, 0.3), 0 0 0.5px rgba(255, 255, 255, 0.8), inset 0 0 0.5px rgba(255, 255, 255, 0.8)',
    position: 'fixed' as const,
    bottom: '20px',
    right: '20px',
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    display: 'flex',
    zIndex: 10000,
    cursor: getIsEnterTimeoutActive() ? 'not-allowed' : 'pointer',
    overflow: 'hidden',
    opacity: isReady ? 1 : 0,
    transition: isReady ? 'opacity 0.1s ease-in' : 'none',
  };

  // Pseudo-element effect
  const glassAfterStyle = {
    content: '""',
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '50%',
    backdropFilter: 'blur(1px)',
    boxShadow: 'inset -10px -8px 0px -11px rgba(255, 255, 255, 1), inset 0px -9px 0px -8px rgba(255, 255, 255, 1)',
    opacity: 0.6,
    zIndex: -1,
    filter: 'blur(1px) drop-shadow(6px 2px 5px #333333) brightness(115%)',
    pointerEvents: 'none' as const
  };

  // Speech bubble style
  const bubbleStyle = {
    position: 'fixed' as const,
    bottom: '95px', 
    right: '23px',
    zIndex: 10001,
    textAlign: 'center' as const,
    lineHeight: '1',
    margin: '0',
    backgroundColor: '#fff',
    border: '1.5px solid #666',
    fontFamily: 'sans-serif',
    fontSize: '14px',
    fontWeight: 400,
    width: '50px',
    height: '49px',
    borderRadius: '30px',
    padding: '8px 6px 7px 6px',
    color: '#555',
    opacity: isReady ? 1 : 0,
    transition: isReady ? 'opacity 0.1s ease-in' : 'none',
    // Plug the fractional 1.5px gap
    boxShadow: '0 0 0.5px #666, inset 0 0 0.5px #fff',
  };

  // Don't render anything until ready
  if (!isReady) {
    return null;
  }

  return (
    <>
      {/* Speech bubble - only show when chat is NOT active */}
      {!isChatActive && (
        <p 
          className="bubble"
          style={bubbleStyle}
        >
          i'm you
        </p>
      )}
      
      <div
        className='cursor'
        style={glassStyle}
        onClick={getIsEnterTimeoutActive() ? undefined : onSwitchCursor} // Conditionally disable onClick
      >
        <div style={glassAfterStyle}></div>
        
        {/* Render Sprite Div */}
        <div
          className={isMoving ? (isSlowFrame ? "cursor-walking-slow" : "cursor-walking") : ""}
          style={{
            width: '40px',
            height: '40px',
            backgroundImage: `url(${MASTER_SPRITE_URL})`,
            backgroundSize: "80px 400px",
            backgroundRepeat: "no-repeat",
            "--cursor-y": `-${currentCursor.spriteY}px`,
            backgroundPosition: `0px var(--cursor-y)`,
            filter: "drop-shadow(1px 1px 1px rgba(0,0,0,0.3))",
            pointerEvents: 'none',
            transform: `translateZ(0) ${isFlipped ? 'scaleX(-1)' : ''}`,
            WebkitTransform: `translateZ(0) ${isFlipped ? 'scaleX(-1)' : ''}`,
            position: 'absolute',
            top: '45%',
            left: '51%',
            marginTop: '-20px',
            marginLeft: '-20px',
          } as React.CSSProperties}
        />
      </div>
      
      {/* CSS for bubble pseudo-elements AND cursor animations */}
      <style>{`
        p.bubble:before,
        p.bubble:after {
          content: ' ';
          position: absolute;
          width: 0;
          height: 0;
        }
        p.bubble:before,
        p.bubble:after {
          left: 9px;
          bottom: -3px;
          width: 7px;
          height: 7px;
          background-color: #fff;
          border: 1.5px solid #666;
          -webkit-border-radius: 28px;
          -moz-border-radius: 28px;
          border-radius: 32px;
        }
        p.bubble:after {
          width: 4px;
          height: 4px;
          left: 8px;
          bottom: -7px;
          -webkit-border-radius: 18px;
          -moz-border-radius: 18px;
          border-radius: 18px;
        }
        
        /* Cursor Walk Animation Styles */
        @keyframes cursor-walk {
          100% { background-position: -80px var(--cursor-y); }
        }
        .cursor-walking {
          animation: cursor-walk 0.16s steps(2) infinite;
        }
        .cursor-walking-slow {
          animation: cursor-walk 1s steps(2) infinite;
        }
      `}</style>
    </>
  );
}